import { defineStore } from 'pinia'
import { getTableMapDefaultSize, tableTemplateTableTypes } from 'src/common/constants'
import type {
  ITableTemplateElement,
  ITableTemplateFloor,
  TableTemplateAlignDirection,
  TableTemplateElementType,
} from 'src/common/interfaces'

interface IState {
  floors: ITableTemplateFloor[]
  activeFloorId: number
  selectedElementIds: string[]
  zoom: number
  history: ITableTemplateFloor[][]
  historyIndex: number
  pendingDelete: string[] | null
  focusElementId: string | null
}

const MAX_HISTORY = 50

function isTableType(type: TableTemplateElementType): boolean {
  return tableTemplateTableTypes.includes(type)
}

// Locked elements reject every change except unlocking (see setElementsLocked)
function isEditable(element: ITableTemplateElement): boolean {
  return !element.is_locked
}

function getVisualBounds(element: ITableTemplateElement) {
  const scale = element.scale ?? 1
  const scaledWidth = element.width * scale
  const scaledHeight = element.height * scale
  const centerX = element.x + scaledWidth / 2
  const centerY = element.y + scaledHeight / 2
  if (isTableType(element.type) || !element.rotation) {
    return { x: element.x, y: element.y, width: scaledWidth, height: scaledHeight, centerX, centerY }
  }
  const radians = (element.rotation * Math.PI) / 180
  const rotatedWidth = Math.abs(scaledWidth * Math.cos(radians)) + Math.abs(scaledHeight * Math.sin(radians))
  const rotatedHeight = Math.abs(scaledWidth * Math.sin(radians)) + Math.abs(scaledHeight * Math.cos(radians))
  return {
    x: centerX - rotatedWidth / 2,
    y: centerY - rotatedHeight / 2,
    width: rotatedWidth,
    height: rotatedHeight,
    centerX,
    centerY,
  }
}

export const useTableTemplateStore = defineStore('tableTemplate', {
  state: (): IState => ({
    floors: [],
    activeFloorId: 0,
    selectedElementIds: [],
    zoom: 1,
    history: [[]],
    historyIndex: 0,
    pendingDelete: null,
    focusElementId: null,
  }),

  getters: {
    activeFloor(state): ITableTemplateFloor | undefined {
      return state.floors.find((floor) => floor.id === state.activeFloorId)
    },
    activeElements(state): ITableTemplateElement[] {
      return state.floors.find((floor) => floor.id === state.activeFloorId)?.json_data ?? []
    },
    selectedElement(state): ITableTemplateElement | undefined {
      if (state.selectedElementIds.length !== 1) return undefined
      const elements = state.floors.find((floor) => floor.id === state.activeFloorId)?.json_data ?? []
      return elements.find((element) => element.element_id === state.selectedElementIds[0])
    },
    selectedElements(state): ITableTemplateElement[] {
      const ids = new Set(state.selectedElementIds)
      const elements = state.floors.find((floor) => floor.id === state.activeFloorId)?.json_data ?? []
      return elements.filter((element) => ids.has(element.element_id))
    },
    isMultiSelect(state): boolean {
      return state.selectedElementIds.length > 1
    },
    canUndo(state): boolean {
      return state.historyIndex > 0
    },
    canRedo(state): boolean {
      return state.historyIndex < state.history.length - 1
    },
  },

  actions: {
    init(floors: ITableTemplateFloor[]) {
      this.floors = floors.map((floor) => ({ ...floor, json_data: floor.json_data ?? [] }))
      this.activeFloorId = floors.length > 0 ? floors[0].id : 0
      this.selectedElementIds = []
      this.history = [JSON.parse(JSON.stringify(this.floors))]
      this.historyIndex = 0
    },

    setActiveFloor(id: number) {
      this.activeFloorId = id
      this.selectedElementIds = []
    },

    addElement(type: TableTemplateElementType, shape: string, value: number, x: number, y: number) {
      const floor = this.floors.find((floor) => floor.id === this.activeFloorId)
      if (!floor) return
      const size = getTableMapDefaultSize(type, shape, value)
      const existingZIndexes = floor.json_data.map((element) => element.z_index)
      const zIndex =
        existingZIndexes.length === 0
          ? 500
          : type === 'flooring'
            ? Math.min(...existingZIndexes) - 1
            : Math.max(...existingZIndexes) + 1

      const element: ITableTemplateElement = {
        element_id: `el-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        type,
        shape,
        value,
        x,
        y,
        rotation: 0,
        floor_id: floor.id,
        table_number: isTableType(type) ? this._nextTableNumber() : 0,
        section_id: 0,
        width: size.width,
        height: size.height,
        z_index: zIndex,
        scale: 1,
        no_seat: false,
        flip_x: false,
        flip_y: false,
        is_locked: false,
        render_x: null,
        render_y: null,
        is_circle: false,
      }
      floor.json_data.push(element)
      this.selectedElementIds = [element.element_id]
      this._pushHistory()
    },

    moveElements(positions: Record<string, { x: number; y: number }>) {
      const floor = this.floors.find((floor) => floor.id === this.activeFloorId)
      if (!floor) return
      for (const element of floor.json_data) {
        const position = positions[element.element_id]
        if (position && isEditable(element)) {
          element.x = position.x
          element.y = position.y
        }
      }
    },

    resizeAndMoveElement(id: string, x: number, y: number, width: number, height: number) {
      const element = this._findElement(id)
      if (element && isEditable(element)) {
        element.x = x
        element.y = y
        element.width = width
        element.height = height
      }
    },

    finishMove() {
      this._pushHistory()
    },

    updateElement(id: string, patch: Partial<ITableTemplateElement>) {
      const element = this._findElement(id)
      if (element && isEditable(element)) {
        Object.assign(element, patch)
        this._pushHistory()
      }
    },

    setElementsLocked(ids: string[], isLocked: boolean) {
      const elements = this.activeElements.filter(
        (element) => ids.includes(element.element_id) && element.is_locked !== isLocked,
      )
      if (!elements.length) return
      elements.forEach((element) => (element.is_locked = isLocked))
      this._pushHistory()
    },

    setElementScale(id: string, scale: number) {
      const element = this._findElement(id)
      if (!element || !isEditable(element)) return
      element.scale = scale
    },

    setSelectedElementsScale(scale: number) {
      const elements = this.selectedElements.filter(isEditable)
      if (!elements.length) return
      const minX = Math.min(...elements.map((element) => element.x))
      const minY = Math.min(...elements.map((element) => element.y))
      const currentScale = elements[0].scale ?? 1
      const factor = scale / currentScale
      elements.forEach((element) => {
        element.x = minX + (element.x - minX) * factor
        element.y = minY + (element.y - minY) * factor
        element.scale = scale
      })
    },

    rotateSelectedElements() {
      const elements = this.selectedElements.filter(isEditable)
      if (!elements.length) return

      if (elements.length === 1) {
        elements[0].rotation = (elements[0].rotation + 90) % 360
        this._pushHistory()
        return
      }

      // Measure pre-rotation gaps along both axes independently
      const sortedByX = [...elements].sort((a, b) => getVisualBounds(a).x - getVisualBounds(b).x)
      let totalHGap = 0
      for (let index = 1; index < sortedByX.length; index++) {
        const boundsA = getVisualBounds(sortedByX[index - 1])
        const boundsB = getVisualBounds(sortedByX[index])
        totalHGap += boundsB.x - (boundsA.x + boundsA.width)
      }
      const avgHGap = totalHGap / (sortedByX.length - 1)

      const sortedByY = [...elements].sort((a, b) => getVisualBounds(a).y - getVisualBounds(b).y)
      let totalVGap = 0
      for (let index = 1; index < sortedByY.length; index++) {
        const boundsA = getVisualBounds(sortedByY[index - 1])
        const boundsB = getVisualBounds(sortedByY[index])
        totalVGap += boundsB.y - (boundsA.y + boundsA.height)
      }
      const avgVGap = totalVGap / (sortedByY.length - 1)

      // Rotate each element in place
      elements.forEach((element) => {
        element.rotation = (element.rotation + 90) % 360
      })

      // Redistribute H if elements were horizontally separated before rotation
      if (avgHGap >= 0) {
        sortedByX.sort((a, b) => getVisualBounds(a).x - getVisualBounds(b).x)
        const firstHorizontalBounds = getVisualBounds(sortedByX[0])
        let cursor = firstHorizontalBounds.x + firstHorizontalBounds.width + avgHGap
        for (let index = 1; index < sortedByX.length; index++) {
          const visualBounds = getVisualBounds(sortedByX[index])
          const scaledWidth = sortedByX[index].width * (sortedByX[index].scale ?? 1)
          sortedByX[index].x = cursor + visualBounds.width / 2 - scaledWidth / 2
          cursor += visualBounds.width + avgHGap
        }
      }

      // Redistribute V if elements were vertically separated before rotation
      if (avgVGap >= 0) {
        sortedByY.sort((a, b) => getVisualBounds(a).y - getVisualBounds(b).y)
        const firstVerticalBounds = getVisualBounds(sortedByY[0])
        let cursor = firstVerticalBounds.y + firstVerticalBounds.height + avgVGap
        for (let index = 1; index < sortedByY.length; index++) {
          const visualBounds = getVisualBounds(sortedByY[index])
          const scaledHeight = sortedByY[index].height * (sortedByY[index].scale ?? 1)
          sortedByY[index].y = cursor + visualBounds.height / 2 - scaledHeight / 2
          cursor += visualBounds.height + avgVGap
        }
      }

      this._pushHistory()
    },

    flipSelectedElements(axis: 'horizontal' | 'vertical') {
      const elements = this.selectedElements.filter((element) => !isTableType(element.type) && isEditable(element))
      if (!elements.length) return
      elements.forEach((element) => {
        if (axis === 'horizontal') element.flip_x = !element.flip_x
        else element.flip_y = !element.flip_y
        // Mirroring across a screen axis reverses the rotation direction, so the flip
        // stays screen-aligned (left↔right / top↔bottom) even for rotated elements
        element.rotation = (360 - (element.rotation % 360)) % 360
      })
      this._pushHistory()
    },

    requestDelete(ids: string[]) {
      // Filtered here, before the page archives the pending ids on the server
      const deletableIds = ids.filter((id) => {
        const element = this._findElement(id)
        return element && isEditable(element)
      })
      if (deletableIds.length) this.pendingDelete = deletableIds
    },

    confirmDelete() {
      if (!this.pendingDelete) return
      const ids = this.pendingDelete
      this.pendingDelete = null
      this.deleteElements(ids)
    },

    cancelDelete() {
      this.pendingDelete = null
    },

    selectElement(id: string | null) {
      this.selectedElementIds = id ? [id] : []
    },

    toggleElementSelection(id: string) {
      const elementIndex = this.selectedElementIds.indexOf(id)
      if (elementIndex === -1) {
        this.selectedElementIds = [...this.selectedElementIds, id]
      } else {
        this.selectedElementIds = this.selectedElementIds.filter((elementId) => elementId !== id)
      }
    },

    deleteElements(ids: string[]) {
      if (!this.activeFloor) return
      this.discardElements(ids)
      this._pushHistory()
    },

    // Removes elements without recording history, for changes that were never committed (e.g. a cancelled Alt-drag)
    discardElements(ids: string[]) {
      const floor = this.floors.find((floor) => floor.id === this.activeFloorId)
      if (!floor) return
      const idSet = new Set(ids)
      floor.json_data = floor.json_data.filter((element) => !idSet.has(element.element_id))
      this.selectedElementIds = this.selectedElementIds.filter((id) => !idSet.has(id))
    },

    duplicateElements(ids: string[]) {
      if (!this.activeFloor) return
      this.selectedElementIds = Object.values(this._cloneElements(ids, 30))
      this._pushHistory()
    },

    // Alt-drag: copies the elements in place and selects the copies. Records no history, since the drag that
    // moves the copies does when it ends (finishMove); returns the copy's id for each source id
    cloneElements(ids: string[]): Record<string, string> {
      const cloneIds = this._cloneElements(ids, 0)
      this.selectedElementIds = Object.values(cloneIds)
      return cloneIds
    },

    distributeWithGap(gap: number, axis: 'horizontal' | 'vertical') {
      const elements = this.selectedElements.filter(isEditable)
      if (elements.length < 2) return
      if (axis === 'horizontal') {
        elements.sort((a, b) => getVisualBounds(a).x - getVisualBounds(b).x)
        const firstBounds = getVisualBounds(elements[0])
        let cursor = firstBounds.x + firstBounds.width + gap
        for (let index = 1; index < elements.length; index++) {
          const visualBounds = getVisualBounds(elements[index])
          const scaledWidth = elements[index].width * (elements[index].scale ?? 1)
          elements[index].x = cursor + visualBounds.width / 2 - scaledWidth / 2
          cursor += visualBounds.width + gap
        }
      } else {
        elements.sort((a, b) => getVisualBounds(a).y - getVisualBounds(b).y)
        const firstBounds = getVisualBounds(elements[0])
        let cursor = firstBounds.y + firstBounds.height + gap
        for (let index = 1; index < elements.length; index++) {
          const visualBounds = getVisualBounds(elements[index])
          const scaledHeight = elements[index].height * (elements[index].scale ?? 1)
          elements[index].y = cursor + visualBounds.height / 2 - scaledHeight / 2
          cursor += visualBounds.height + gap
        }
      }
      this._pushHistory()
    },

    alignElements(direction: TableTemplateAlignDirection) {
      const elements = this.selectedElements
      if (elements.length < 2 || !elements.some(isEditable)) return
      const bounds = elements.map((element) => getVisualBounds(element))
      const minX = Math.min(...bounds.map((b) => b.x))
      const maxRight = Math.max(...bounds.map((b) => b.x + b.width))
      const minY = Math.min(...bounds.map((b) => b.y))
      const maxBottom = Math.max(...bounds.map((b) => b.y + b.height))
      const centerX = (minX + maxRight) / 2
      const centerY = (minY + maxBottom) / 2
      // Locked elements still count towards the bounds, so the rest align to them
      elements.forEach((element, index) => {
        if (!isEditable(element)) return
        const elementBounds = bounds[index]
        const scaledWidth = element.width * (element.scale ?? 1)
        const scaledHeight = element.height * (element.scale ?? 1)
        if (direction === 'left') element.x = minX + elementBounds.width / 2 - scaledWidth / 2
        else if (direction === 'center-h') element.x = Math.round(centerX - scaledWidth / 2)
        else if (direction === 'right') element.x = maxRight - elementBounds.width / 2 - scaledWidth / 2
        else if (direction === 'top') element.y = minY + elementBounds.height / 2 - scaledHeight / 2
        else if (direction === 'center-v') element.y = Math.round(centerY - scaledHeight / 2)
        else if (direction === 'bottom') element.y = maxBottom - elementBounds.height / 2 - scaledHeight / 2
      })
      this._pushHistory()
    },

    setZoom(zoom: number) {
      this.zoom = Math.max(0.15, Math.min(3, zoom))
    },

    reorderElements(orderedIds: string[]) {
      // orderedIds[0] = frontmost (highest z_index)
      const floor = this.floors.find((floor) => floor.id === this.activeFloorId)
      if (!floor) return
      orderedIds.forEach((id, index) => {
        const element = floor.json_data.find((existingElement) => existingElement.element_id === id)
        if (element) element.z_index = orderedIds.length - index
      })
      this._pushHistory()
    },

    focusElement(id: string) {
      this.selectedElementIds = [id]
      this.focusElementId = id
    },

    clearFocusElementId() {
      this.focusElementId = null
    },

    undo() {
      if (!this.canUndo) return
      this.historyIndex--
      this.floors = JSON.parse(JSON.stringify(this.history[this.historyIndex]))
      this.selectedElementIds = []
    },

    redo() {
      if (!this.canRedo) return
      this.historyIndex++
      this.floors = JSON.parse(JSON.stringify(this.history[this.historyIndex]))
      this.selectedElementIds = []
    },

    isDuplicateTableNumber(currentElement: ITableTemplateElement): boolean {
      if (currentElement.type !== 'table' && currentElement.type !== 'seat') return false

      return this.floors
        .flatMap((floor) => floor.json_data)
        .some(
          (element) =>
            element.element_id !== currentElement.element_id && element.table_number === currentElement.table_number,
        )
    },

    _findElement(id: string): ITableTemplateElement | undefined {
      const floor = this.floors.find((floor) => floor.id === this.activeFloorId)
      return (floor?.json_data ?? []).find((element) => element.element_id === id)
    },

    // Copies are unlocked, get a new table number when they're tables, and are shifted by `offset` on both axes
    _cloneElements(ids: string[], offset: number): Record<string, string> {
      const floor = this.floors.find((floor) => floor.id === this.activeFloorId)
      const cloneIds: Record<string, string> = {}
      if (!floor) return cloneIds
      for (const id of ids) {
        const element = floor.json_data.find((existingElement) => existingElement.element_id === id)
        if (!element) continue
        const newElement: ITableTemplateElement = {
          ...element,
          id: -1,
          element_id: `el-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          x: element.x + offset,
          y: element.y + offset,
          table_number: isTableType(element.type) ? this._nextTableNumber() : element.table_number,
          is_locked: false,
        }
        delete newElement.id
        floor.json_data.push(newElement)
        cloneIds[id] = newElement.element_id
      }
      return cloneIds
    },

    _nextTableNumber(): number {
      const all = this.floors.flatMap((floor) => floor.json_data)
      const tableNumbers = all
        .filter((element) => isTableType(element.type) && element.table_number)
        .map((element) => element.table_number)
      return tableNumbers.length > 0 ? Math.max(...tableNumbers) + 1 : 1
    },

    _pushHistory() {
      const snapshot = JSON.parse(JSON.stringify(this.floors))
      this.history = this.history.slice(0, this.historyIndex + 1)
      this.history.push(snapshot)
      if (this.history.length > MAX_HISTORY) {
        this.history.shift()
      } else {
        this.historyIndex++
      }
    },
  },
})
